import { Box, Card, Icon, IconTile, Text, type IconName } from "@salestrak/ui";
import { ScreenHeader } from "./admin-ui";
import type { AdminScreen } from "./admin-console";
import type { Omittable } from "@salestrak/ui";

function OverviewCard({
  icon,
  label,
  value,
  hint,
  onClick,
}: Omittable<{
  icon: IconName;
  label: string;
  value: string;
  hint?: string;
  onClick: () => void;
}>) {
  return (
    // Card has no `pressable`/`padding`/`fill` props — it renders as a
    // real button element instead, and Box supplies the layout.
    <Card
      render={
        <Box as="button" type="button" padding="md" grow gap="xs" cursor="pointer" />
      }
      onClick={onClick}
      className="rounded-lg text-start hover:bg-accent/50"
    >
      <Box direction="row" align="center" gap="sm">
        <IconTile color="muted" name={icon} />
        <Text size="sm" variant="muted">
          {label}
        </Text>
      </Box>
      <Text size="2xl" weight="semibold" mono>
        {value}
      </Text>
      {hint ? (
        <Text size="xs" variant="muted">
          {hint}
        </Text>
      ) : null}
    </Card>
  );
}

export function OverviewScreen({
  superuserCount,
  flagCount,
  workspaceCount,
  servicesUp,
  servicesTotal,
  navigate,
}: {
  superuserCount: number;
  flagCount: number;
  workspaceCount: number;
  servicesUp: number;
  servicesTotal: number;
  navigate: (screen: AdminScreen) => void;
}) {
  return (
    <Box fill="flex" overflow="auto">
      <ScreenHeader title="Overview" meta="Platform at a glance" />
      <Box paddingX="lg" paddingTop="sm" gap="sm">
        <Box direction="row" gap="sm">
          <OverviewCard
            icon="shield"
            label="Superusers"
            value={String(superuserCount)}
            hint="With platform access"
            onClick={() => navigate("superusers")}
          />
          <OverviewCard
            icon="flag"
            label="Feature flags"
            value={String(flagCount)}
            hint="Defined in code"
            onClick={() => navigate("flags")}
          />
          <OverviewCard
            icon="layout-grid"
            label="Workspaces"
            value={String(workspaceCount)}
            hint="Active on the platform"
            onClick={() => navigate("workspaces")}
          />
          <OverviewCard
            icon="activity"
            label="Services up"
            value={`${servicesUp}/${servicesTotal}`}
            hint="Live health"
            onClick={() => navigate("health")}
          />
        </Box>
        <Box padding="lg">
          <Box align="center" gap="xs">
            <Icon name="layout-dashboard" size="2xl" color="muted" />
            <Text size="base" weight="semibold">
              Admin console
            </Text>
            <Text size="sm" variant="muted" align="center">
              Manage superusers, feature flags, workspaces, and platform health.
              Use the sidebar to navigate.
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
